import apiService from './apiService';

export interface CreditNoteItem {
  id?: string;
  description: string;
  itemCode?: string | null;
  subcategory?: string | null;
  quantity: number;
  unit?: string | null;
  unitPrice: number;
  discount?: number;
  discountType?: 'fixed' | 'percentage';
  taxRate?: number;
  taxAmount?: number;
  lineTotal: number;
  accountId?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  sortOrder?: number;
}

export interface CreditNote {
  id: string;
  creditNoteNo: string;
  customerId: string;
  userId: string;
  companyId: string;
  invoiceId?: string | null;
  status: 'DRAFT' | 'POSTED' | 'CANCELLED';
  reason?: string | null;
  creditNoteDate: string;
  totalAmount: number;
  currency: string;
  exchangeRate?: number | null;
  notes?: string | null;
  termsConditions?: string | null;
  postedAt?: string | null;
  postedBy?: string | null;
  journalEntryId?: string | null;
  createdAt: string;
  updatedAt: string;
  items: CreditNoteItem[];
  customer?: { id: string; name: string; email?: string | null };
  invoice?: { id: string; invoiceNo: string; status?: string; totalAmount?: number; currency?: string } | null;
}

export interface CreateCreditNoteInput {
  customerId: string;
  invoiceId?: string | null;
  creditNoteDate?: string;
  totalAmount: number;
  currency?: string;
  exchangeRate?: number;
  reason?: string | null;
  notes?: string | null;
  termsConditions?: string | null;
  items: Omit<CreditNoteItem, 'id'>[];
}

export interface UpdateCreditNoteInput extends Partial<CreateCreditNoteInput> {
  status?: 'DRAFT' | 'POSTED' | 'CANCELLED';
}

export interface PostCreditNoteInput {
  accountsReceivableAccountId: string;
  revenueAccountId: string;
  journalId: string;
}

export interface CreditNoteFilters {
  customerId?: string;
  status?: string;
  invoiceId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface CreditNoteStats {
  totalCreditNotes: number;
  draftCount: number;
  postedCount: number;
  cancelledCount: number;
  totalAmount: number;
  postedAmount: number;
}

class CreditNoteService {
  private baseUrl = '/accounting/credit-notes';

  async getAllCreditNotes(filters?: CreditNoteFilters): Promise<CreditNote[]> {
    const params = new URLSearchParams();
    if (filters?.customerId) params.append('customerId', filters.customerId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.invoiceId) params.append('invoiceId', filters.invoiceId);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.search) params.append('search', filters.search);

    const qs = params.toString();
    const url = qs ? `${this.baseUrl}?${qs}` : this.baseUrl;
    const response = await apiService.get(url);
    return response.data || [];
  }

  async getCreditNoteById(id: string): Promise<CreditNote> {
    const response = await apiService.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async getCreditNoteByNumber(creditNoteNo: string): Promise<CreditNote> {
    const response = await apiService.get(`${this.baseUrl}/number/${creditNoteNo}`);
    return response.data;
  }

  async getCreditNotesByCustomer(customerId: string): Promise<CreditNote[]> {
    const response = await apiService.get(`${this.baseUrl}/customer/${customerId}`);
    return response.data;
  }

  async getStats(): Promise<CreditNoteStats> {
    const response = await apiService.get(`${this.baseUrl}/stats`);
    return response.data;
  }

  async createCreditNote(data: CreateCreditNoteInput): Promise<CreditNote> {
    const response = await apiService.post(this.baseUrl, data);
    return response.data;
  }

  async createFromInvoice(invoiceId: string): Promise<CreditNote> {
    const response = await apiService.post(`${this.baseUrl}/from-invoice/${invoiceId}`, {});
    return response.data;
  }

  async updateCreditNote(id: string, data: UpdateCreditNoteInput): Promise<CreditNote> {
    const response = await apiService.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  async postCreditNote(id: string, data: PostCreditNoteInput): Promise<CreditNote> {
    const response = await apiService.post(`${this.baseUrl}/${id}/post`, data);
    return response.data;
  }

  async cancelCreditNote(id: string, data: PostCreditNoteInput): Promise<CreditNote> {
    const response = await apiService.post(`${this.baseUrl}/${id}/cancel`, data);
    return response.data;
  }

  async deleteCreditNote(id: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Download credit-note PDF. Returns the blob and the server-suggested
   * filename from Content-Disposition (caller falls back if absent).
   */
  async downloadPdf(id: string): Promise<{ blob: Blob; filename: string | null }> {
    const token = localStorage.getItem('authToken');
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
    const response = await fetch(`${apiBaseUrl}/accounting/credit-notes/${id}/pdf/download`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) {
      throw new Error('Failed to download credit-note PDF');
    }
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
    const filename = match ? decodeURIComponent(match[1].trim()) : null;
    return { blob: await response.blob(), filename };
  }
}

export default new CreditNoteService();
